/**
 * Multi-instance slides (manifest `multi: N`) expand to N entries sharing
 * one file — one manifest row. Reordering operates on these blocks, never
 * inside them, and the reorder endpoint wants one id per row.
 */

import type { SlideEntry } from '../../shared/deck.ts';

/** A run of consecutive entries sharing one manifest row. */
export type SlideBlock = {
  baseId: string;
  section: string;
  items: SlideEntry[];
};

/** The manifest row a `base#instance` id belongs to. `split` always yields a
 *  first element; `?? id` states that without an assertion.
 *
 *  Exported because the editor does this in eight places, and every one of
 *  them is the same question: which FILE does this slide instance live in. */
export const baseOf = (id: string): string => id.split('#')[0] ?? id;

/** Group a flat slide list into consecutive same-file blocks. */
export function groupBlocks(slides: SlideEntry[] | null | undefined): SlideBlock[] {
  const out: SlideBlock[] = [];
  for (const s of slides || []) {
    const baseId = baseOf(s.id);
    const last = out[out.length - 1];
    if (last && last.baseId === baseId) last.items.push(s);
    else out.push({ baseId, section: s.section, items: [s] });
  }
  return out;
}

/**
 * Resolve a block-space move into the payload `commitReorder` wants.
 *
 * `fromIdx`/`toIdx` are dnd-kit's active/over indices — `toIdx` is the moved
 * block's FINAL position, which is what `splice`-after-removal already means.
 * The drop position decides the section: the previous neighbour wins, falling
 * back to the next one at the very top of the list. Returns null for a no-op
 * so both surfaces can bail without duplicating the check.
 */
export function resolveReorder(
  blocks: SlideBlock[],
  fromIdx: number,
  toIdx: number,
): { newList: SlideEntry[]; moved: SlideBlock; toSection: string | null } | null {
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return null;
  const base = blocks[fromIdx];
  if (!base) return null;
  const res = resolveMultiReorder(blocks, [base.baseId], fromIdx, toIdx);
  // `moved` is non-empty whenever resolveMultiReorder returns (it bails on an
  // empty match), but only the guard makes that readable to the checker.
  const moved = res?.moved[0];
  if (!res || !moved) return null;
  return { newList: res.newList, moved, toSection: res.toSection };
}

/** One slide that changed section, with the section it came FROM — the commit
 *  path retags per origin so an undo can put each back where it was. */
export type SectionMove = { slide: SlideEntry; from: string };

export type MultiReorder = {
  newList: SlideEntry[];
  moved: SlideBlock[];
  /** The section the run landed in, or null when nothing changed section —
   *  the caller reads this as "is there a retag to commit at all". */
  toSection: string | null;
  sectionMoves: SectionMove[];
};

/**
 * Multi-block variant: lift every block in `movedIds` out (document order,
 * selection order never matters) and insert them contiguously at the slot the
 * ACTIVE block would land in — dragging down drops the run after the over
 * block, dragging up before it, which reproduces the single-block
 * splice-after-removal rule exactly. Returns null for no-ops (over a moved
 * block, or the order is unchanged — e.g. dropped between two moved
 * neighbours). `sectionMoves` records every slide whose section changed, with
 * its original section, so the commit path can retag and undo per origin.
 */
export function resolveMultiReorder(
  blocks: SlideBlock[],
  movedIds: Iterable<string> | Set<string>,
  activeIdx: number,
  overIdx: number,
): MultiReorder | null {
  if (activeIdx < 0 || overIdx < 0) return null;
  const ids = movedIds instanceof Set ? movedIds : new Set(movedIds);
  const overBlock = blocks[overIdx];
  if (!overBlock || ids.has(overBlock.baseId)) return null;
  const movedBlocks = blocks.filter((b) => ids.has(b.baseId));
  const firstMoved = movedBlocks[0];
  if (!firstMoved) return null;

  const remaining = blocks.filter((b) => !ids.has(b.baseId));
  const insertAt = remaining.indexOf(overBlock) + (activeIdx < overIdx ? 1 : 0);
  const newBlocks = remaining.slice();
  newBlocks.splice(insertAt, 0, ...movedBlocks);
  if (newBlocks.every((b, i) => b === blocks[i])) return null;

  const neighbour = newBlocks[insertAt - 1] || newBlocks[insertAt + movedBlocks.length];
  const toSection = neighbour ? neighbour.section : firstMoved.section;
  const movedSet = new Set(movedBlocks);
  const sectionMoves: SectionMove[] = [];
  const newList = newBlocks
    .flatMap((b) => {
      if (!movedSet.has(b) || b.section === toSection) return b.items;
      sectionMoves.push(...b.items.map((s) => ({ slide: s, from: b.section })));
      return b.items.map((s) => ({ ...s, section: toSection }));
    })
    .map((s, i) => ({ ...s, idx: i }));

  return { newList, moved: movedBlocks, toSection: sectionMoves.length ? toSection : null, sectionMoves };
}

/** A section-divider stub — structural filler the server refuses to copy into
 *  the library (routes/library.ts). Mirrors the server's slideBasename check. */
export function isDivider(slide: { id?: string } | null | undefined): boolean {
  // `.jsx` is correct and stays: this matches a DECK slide's id, and decks
  // keep their .jsx extension (see the overhaul plan, decision 4).
  return /(^|\/)divider\.jsx$/.test(baseOf(String(slide?.id || '')));
}

/** One base id per manifest row, in list order — the reorder payload. */
export function baseOrder(slides: SlideEntry[] | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of slides || []) {
    const base = baseOf(s.id);
    if (!seen.has(base)) {
      seen.add(base);
      out.push(base);
    }
  }
  return out;
}
